import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TemplateList } from "./template-management/TemplateList";
import { TemplateVersioning } from "./template-management/TemplateVersioning";
import { LayoutTemplate, FileText, TestTube2 } from "lucide-react";
import { NewsletterTemplate, NewsletterTemplateVersion } from "./types";
import { Json } from "@/integrations/supabase/types/common";
import { toast } from "sonner";

export const NewsletterTemplatesTab = () => {
  const { data: templates, isLoading } = useQuery({
    queryKey: ['newsletter-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('newsletter_templates')
        .select(`
          *,
          newsletter_template_versions (*)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      return data.map((template): NewsletterTemplate => {
        const contentStructure = template.content_structure as { template?: string; variables?: Record<string, Json> };
        
        return {
          ...template,
          content_structure: {
            template: typeof contentStructure?.template === 'string' ? 
              contentStructure.template : '',
            variables: contentStructure?.variables || {}
          },
          newsletter_template_versions: template.newsletter_template_versions?.map((version): NewsletterTemplateVersion => {
            const versionContent = version.content_structure as { template?: string; variables?: Record<string, Json> };
            const metrics = version.performance_metrics as { 
              open_rate?: number; 
              click_rate?: number; 
              engagement_score?: number; 
            } || {};

            return {
              ...version,
              content_structure: {
                template: typeof versionContent?.template === 'string' ? 
                  versionContent.template : '',
                variables: versionContent?.variables || {}
              },
              performance_metrics: {
                open_rate: Number(metrics.open_rate) || 0,
                click_rate: Number(metrics.click_rate) || 0,
                engagement_score: Number(metrics.engagement_score) || 0
              }
            };
          }) || []
        };
      });
    },
  });

  const handleViewTemplate = (template: NewsletterTemplate) => {
    console.log('View template:', template);
  };

  const handleEditTemplate = (template: NewsletterTemplate) => {
    console.log('Edit template:', template);
  };

  const handleDeleteTemplate = async (template: NewsletterTemplate) => {
    try {
      const { error } = await supabase
        .from('newsletter_templates')
        .delete()
        .eq('id', template.id);

      if (error) throw error;
      toast.success('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <LayoutTemplate className="h-5 w-5 text-primary" />
        <h2 className="text-2xl font-bold tracking-tight">Newsletter Templates</h2>
      </div>
      
      <Tabs defaultValue="list" className="space-y-4">
        <TabsList>
          <TabsTrigger value="list" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="testing" className="flex items-center gap-2">
            <TestTube2 className="h-4 w-4" />
            A/B Testing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !templates?.length ? (
            <Card className="p-8 text-center text-muted-foreground">
              No templates found. Create your first template to get started.
            </Card>
          ) : (
            <TemplateList 
              templates={templates}
              onView={handleViewTemplate}
              onEdit={handleEditTemplate}
              onDelete={handleDeleteTemplate}
              onSelect={(id) => console.log('Selected template:', id)}
              onPreviewTemplate={(template, version) => {
                console.log('Preview template:', template, version);
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="testing">
          <TemplateVersioning templateId={templates?.[0]?.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};